/**
 * @file: types.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { IconType } from '@euler/components/Icomoon';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import { PendingIssueInfoWithDetail } from '@euler/model/PendingIssueInfoWithDetail';
import { MaintenanceServicePlanDetailType } from '@euler/model/enum';
import {
  MaintenancePlan,
  MaintenancePlanDetail,
} from '@euler/model/task-detail';

export type MaintenancePlanListItemPlanInfo = {
  nextServiceDate?: string | Date;
  nextServiceMileage?: number;
  estimatedNextServiceMileage?: number;
  remark?: string;
};

export type MaintenancePlanItemInfo = {
  id: string;
  planInfo?: MaintenancePlanListItemPlanInfo;
  existing?: {
    plan: MaintenancePlan;
    detail: MaintenancePlanDetail;
  };
};

export type MaintenancePlanListItemBase = MaintenancePlanItemInfo & {
  name: string;
  remark?: string;
};

export type CustomMaintenancePlanItem = MaintenancePlanListItemBase & {
  type: 'custom';
  source?: string;
  removable?: boolean;
  requiresNextServiceDate?: boolean;
  label?: string;
};

export type InspectionSubjectMaintenancePlanItem =
  MaintenancePlanListItemBase & {
    type: 'inspection-subject';
    author: string;
    flowId: string;
    iconUrl?: string;
  };

export type ConstructionJobMaintenancePlanItem = MaintenancePlanListItemBase & {
  type: 'construction-job';
  jobId: number;
  author: string;
};

export type PendingIssueMaintenancePlanItem = MaintenancePlanListItemBase & {
  type: 'pending-issue';
  issue: PendingIssueInfoWithDetail;
  author?: string;
  repairConfirmed: boolean;
  unsolvedReason?: string;
  customerSignatureImgUrl?: string;
  customerSignatureUnavailableReason?: string;
  customerSignatureUnavailableRemark?: string;
};

export type FaultLikeSubjectMaintenancePlanItem =
  MaintenancePlanListItemBase & {
    type: 'fault';
    solved?: boolean;
    unsolvedReason?: string;
    faultInspectionItem: FaultInspectionItem;
  };

export type InsuranceExpiryMaintenancePlanItem = MaintenancePlanListItemBase & {
  type: 'insurance-expiry';
  optedIn: boolean;
  removable?: boolean;
};

export const INSURANCE_EXPIRY_ITEM_NAME = '保险到期日期';

export type MaintenancePlanListItem =
  | CustomMaintenancePlanItem
  | InspectionSubjectMaintenancePlanItem
  | ConstructionJobMaintenancePlanItem
  | PendingIssueMaintenancePlanItem
  | FaultLikeSubjectMaintenancePlanItem
  | InsuranceExpiryMaintenancePlanItem;

export type MaintenancePlanListItemType = MaintenancePlanListItem['type'];

export type MaintenancePlanListItemActionInfo = {
  actionType:
    | 'view-detail'
    | 'confirm-repair'
    | 'reset-as-pending-issue'
    | 'remove'
    | 'confirm-solve-fault-like-subject'
    | 'reset-fault-like-subject-unsolved'
    | 'opt-in-insurance-expiry'
    | 'opt-out-insurance-expiry';
  type: 'button' | 'link';
  title: string;
  textColor: string;
  backgroundColor?: string;
  borderColor?: string;
  icon?: IconType;
  iconColor?: string;
  iconSize?: number;
};

export interface MaintenancePlanListItemInfo {
  readonly name: string;
  readonly planDetailType: MaintenanceServicePlanDetailType;
  readonly subTitle: string;
  readonly action: MaintenancePlanListItemActionInfo;
}
